/**
 * User normalizers — the single boundary between raw backend user payloads and
 * the canonical user shape consumed by the rest of the app.
 *
 * Why this exists
 * ---------------
 * The frontend reads `state.user.personalInformation.<field>` in selectors,
 * init thunks, RBAC helpers, analytics, and components. Letting raw API field
 * names flow straight into Redux couples every one of those consumers to the
 * backend's current vocabulary. When the backend renames a field (e.g.
 * `usertype` → `lyt_auth_role`), every consumer breaks.
 *
 * `normalizeUser` codifies the canonical shape and is the ONE place to absorb
 * backend renames. Field assignment is explicit (not a spread) so backend
 * additions do not silently leak into the store and renames are visible here
 * at the boundary.
 *
 * The canonical field names match the existing in-app vocabulary
 * (`usertype`, `user_id`, `inAgency`, etc.) so this layer can be introduced
 * without touching downstream consumers. Renaming the internal vocabulary is
 * intentionally out of scope — the point of this layer is that the internal
 * names no longer have to match the backend.
 */

import { resolveLegacyUserId, resolveUserEmail } from './user-identifier-utils';

/**
 * Normalize a raw `/users/email` response (and any other endpoint that
 * returns a user record) into the canonical shape consumed by the rest of
 * the app.
 *
 * Accepts both the legacy (`usertype`) and current (`lyt_auth_role`) role
 * field names so this works during the backend rollover. Unknown / null
 * inputs are returned unchanged so callers can keep handling error states
 * with their existing checks.
 *
 * @param {Object|null|undefined} raw - Raw API response body.
 * @returns {Object|null|undefined} Canonical user record, or the input
 *                                  unchanged if it is not a plain object.
 */
export const normalizeUser = (raw) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return raw;
    }

    const userEmail = resolveUserEmail(raw);
    const legacyUserId = resolveLegacyUserId(raw);

    return {
        // Identity
        user_email: userEmail,
        user_id: legacyUserId,
        id: raw.id ?? legacyUserId ?? '',
        email: userEmail,
        username: raw.username ?? '',
        firstname: raw.firstname ?? '',
        lastname: raw.lastname ?? '',
        picture: raw.picture ?? '',

        // Role / permissions
        // `lyt_auth_role` is the new backend field; `usertype` is the legacy
        // field. Prefer the new name when both are present.
        usertype: raw.lyt_auth_role ?? raw.usertype ?? '',
        role: raw.role ?? { role: null, roleParse: {} },

        // Agency / jurisdiction
        inAgency: raw.inAgency ?? raw.agencies_id ?? raw.in_agency ?? '',
        jurisdictions: Array.isArray(raw.jurisdictions) ? raw.jurisdictions : [],

        // Account state
        account_approved: raw.account_approved ?? false,

        // Contact / notification preferences (round-tripped via PUT /users/user)
        phone: raw.phone ?? null,
        notification_through: raw.notification_through ?? null,

        // Invitation / approval audit trail
        inviting_user_email: raw.inviting_user_email ?? null,
        approving_user_email: raw.approving_user_email ?? null,

        // Audit timestamps (passed through unchanged)
        last_login: raw.last_login ?? null,
        created_at: raw.created_at ?? null,
        updated_at: raw.updated_at ?? null,
    };
};
